import React, { useMemo, useState } from 'react';
import { Box, Button, Chip, Dialog, DialogContent, DialogTitle, IconButton, InputAdornment, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import WidgetPreview from './WidgetPreview';
import { CATEGORIES, WIDGETS } from './registry';
import { SIZES } from './sizes';
import { isWidgetLocked, roleVisible } from './visibility';

/** Design screen 7: category rail + widget cards with a scaled live preview. */
export default function WidgetGalleryDialog({ open, onClose, organization, layoutIds, onAdd }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [category, setCategory] = useState('wo');
  const [query, setQuery] = useState('');

  const visible = useMemo(() => WIDGETS.filter((entry) => roleVisible(entry, organization)), [organization]);
  const counts = useMemo(() => Object.fromEntries(CATEGORIES.map((cat) => [cat.id, visible.filter((entry) => entry.category === cat.id).length])), [visible]);
  const normalized = query.trim().toLowerCase();
  const shown = visible.filter((entry) => {
    if (normalized) {
      const haystack = `${t(`dashboard.widgets.${entry.id}.title`)} ${t(`dashboard.widgets.${entry.id}.desc`)}`.toLowerCase();
      return haystack.includes(normalized);
    }
    return entry.category === category;
  });

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth={false} PaperProps={{ sx: { width: fullScreen ? '100%' : 1100, maxWidth: '100%', height: fullScreen ? '100%' : 720, maxHeight: '100%', borderRadius: fullScreen ? 0 : '16px' } }} data-testid="widget-gallery">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{t('dashboard.edit.addWidget')}</Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t('dashboard.gallery.hint')}</Typography>
        </Box>
        <IconButton onClick={onClose} aria-label={t('dashboard.edit.close')} data-testid="gallery-close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', gap: 2, p: 0, overflow: 'hidden', flexDirection: fullScreen ? 'column' : 'row' }}>
        <Box sx={{ width: fullScreen ? '100%' : 240, flexShrink: 0, borderRight: fullScreen ? 0 : '1px solid #e2e8f0', p: 2, display: 'flex', flexDirection: fullScreen ? 'row' : 'column', gap: 0.5, overflow: 'auto' }}>
          <TextField
            size="small"
            fullWidth
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('dashboard.gallery.search')}
            inputProps={{ 'data-testid': 'gallery-search' }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>, sx: { borderRadius: '10px' } }}
            sx={{ mb: fullScreen ? 0 : 1, minWidth: fullScreen ? 160 : undefined }}
          />
          {CATEGORIES.map((cat) => {
            const active = !normalized && cat.id === category;
            const Icon = cat.icon;
            return (
              <Box
                key={cat.id}
                component="button"
                type="button"
                data-testid={`gallery-category-${cat.id}`}
                onClick={() => {
                  setQuery('');
                  setCategory(cat.id);
                }}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.9, borderRadius: '8px', border: 0, textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5, fontWeight: active ? 600 : 500, bgcolor: active ? 'rgba(49,130,206,0.10)' : 'transparent', color: active ? '#1a365d' : '#64748b', whiteSpace: 'nowrap' }}
              >
                <Icon sx={{ fontSize: 18 }} />
                <Box sx={{ flex: 1 }}>{t(cat.labelKey)}</Box>
                <Box component="span" sx={{ fontSize: 12, color: '#94a3b8' }}>{counts[cat.id]}</Box>
              </Box>
            );
          })}
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'grid', gridTemplateColumns: fullScreen ? '1fr' : 'repeat(auto-fill, minmax(268px, 1fr))', gap: 2, alignContent: 'start' }}>
          {shown.map((entry) => {
            const locked = isWidgetLocked(entry, organization);
            const added = layoutIds.includes(entry.id);
            const Icon = entry.icon;
            return (
              <Box key={entry.id} data-testid={`gallery-card-${entry.id}`} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, bgcolor: '#fff', position: 'relative' }}>
                <Box sx={{ position: 'relative' }}>
                  <WidgetPreview entry={entry} size={entry.defaultSize} locked={locked} />
                  {locked ? (
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#fff', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                        <LockIcon sx={{ fontSize: 20 }} />
                      </Box>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{t('dashboard.state.locked')}</Typography>
                    </Box>
                  ) : null}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icon sx={{ fontSize: 18, color: '#64748b' }} />
                  <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, flex: 1 }}>
                    {t(`dashboard.widgets.${entry.id}.title`)}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary', minHeight: 34 }}>{t(`dashboard.widgets.${entry.id}.desc`)}</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  {entry.sizes.map((size) => (
                    <Chip key={size} label={t(SIZES[size].labelKey)} size="small" sx={{ height: 20, fontSize: 11, bgcolor: size === entry.defaultSize ? '#1a365d' : '#f8fafc', color: size === entry.defaultSize ? '#fff' : '#64748b' }} />
                  ))}
                  {entry.gate ? <Chip label={t(`dashboard.gates.${entry.gate}`, { defaultValue: entry.gate })} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} /> : null}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 'auto' }}>
                  {locked ? (
                    <>
                      <Button size="small" variant="contained" disabled data-testid={`gallery-add-${entry.id}`} sx={{ borderRadius: '8px' }}>
                        {t('dashboard.gallery.add')}
                      </Button>
                      <Button size="small" variant="text" onClick={() => navigate('/settings')} sx={{ color: 'secondary.main', textTransform: 'none' }}>
                        {t('dashboard.state.enableInSettings')}
                      </Button>
                    </>
                  ) : added ? (
                    <Button size="small" variant="text" disabled startIcon={<CheckIcon />} data-testid={`gallery-add-${entry.id}`} sx={{ color: 'text.secondary' }}>
                      {t('dashboard.gallery.added')}
                    </Button>
                  ) : (
                    <Button size="small" variant="contained" color="secondary" data-testid={`gallery-add-${entry.id}`} onClick={() => onAdd(entry.id, entry.defaultSize)} sx={{ borderRadius: '8px' }}>
                      {t('dashboard.gallery.add')}
                    </Button>
                  )}
                </Box>
              </Box>
            );
          })}
          {shown.length === 0 ? <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>{t('dashboard.search.noResults')}</Typography> : null}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
